import api from './api';

export type CashRegisterStatus = 'open' | 'closed';
export type CloseType = 'partial' | 'final';

export interface CashRegisterUser {
  id: number;
  full_name: string;
  email: string;
}

export interface CashRegister {
  id: number;
  tenant_id: number;
  status: CashRegisterStatus;
  shift_number: number;  // Número de turno del día
  opened_at: string;
  opened_by_user_id: number;
  opened_by?: CashRegisterUser;
  opening_cash: number;
  opening_notes?: string;

  closed_at?: string;
  closed_by_user_id?: number;
  closed_by?: CashRegisterUser;
  closing_cash?: number;
  closing_notes?: string;
  close_type?: CloseType;  // "partial" o "final"
  auto_closed?: boolean;

  expected_cash?: number;
  cash_difference?: number;

  total_sales: number;
  total_cash_sales: number;
  total_card_sales: number;
  total_transfer_sales: number;
  total_withdrawals: number;  // Total de salidas de caja
  total_cash_withdrawals: number;  // Salidas de efectivo
  total_card_withdrawals: number;  // Salidas de tarjeta
  total_transfer_withdrawals: number;  // Salidas de transferencia

  total_cost: number;
  total_expenses: number;  // Gastos operativos (afectan ganancia)
  total_profit: number;

  created_at: string;
}

// === Salidas de Caja ===

// Las categorías las define cada veterinaria (tabla cash_withdrawal_categories), así que
// acá viaja el slug. El tipo (gasto / sueldo / retiro) lo declara la categoría.
export type CashWithdrawalCategory = string;
export type PaymentMethod = 'cash' | 'card' | 'transfer';

export interface CashWithdrawal {
  id: number;
  tenant_id: number;
  cash_register_id: number;
  amount: number;
  category: CashWithdrawalCategory;
  payment_method: PaymentMethod;
  description: string;
  recipient?: string;
  created_by_user_id: number;
  created_by?: { id: number; full_name: string };
  created_at: string;
}

export interface CashWithdrawalCreate {
  amount: number;
  category: CashWithdrawalCategory;
  payment_method?: PaymentMethod;
  description?: string;
  recipient?: string;
}

// Tipo de categoría: define si el monto afecta la ganancia y en qué bloque suma.
//   expense    -> gasto (afecta la ganancia)
//   merchandise-> compra de mercadería: baja la caja pero NO es gasto; el costo llega al vender
//   salary     -> sueldo (afecta la ganancia, se reporta aparte)
//   withdrawal -> retiro (baja el saldo de caja, no afecta la ganancia)
//   supplier_payment -> pago a proveedor (lo maneja Compras, no editable)
export type WithdrawalCategoryKind =
  | 'expense'
  | 'salary'
  | 'merchandise'
  | 'withdrawal'
  | 'supplier_payment';

export interface WithdrawalCategory {
  id: number;
  tenant_id: number;
  slug: string;
  name: string;
  kind: WithdrawalCategoryKind;
  icon?: string | null;
  is_system: boolean;
  is_active: boolean;
  sort_order: number;
  in_use: boolean;
}

export interface WithdrawalCategoryCreate {
  name: string;
  kind: WithdrawalCategoryKind;
  icon?: string;
  sort_order?: number;
}

export interface WithdrawalCategoryUpdate {
  name?: string;
  kind?: WithdrawalCategoryKind;
  icon?: string;
  sort_order?: number;
  is_active?: boolean;
}

export const WITHDRAWAL_KIND_LABELS: Record<WithdrawalCategoryKind, string> = {
  expense: 'Gasto',
  salary: 'Sueldo',
  merchandise: 'Mercadería',
  withdrawal: 'Retiro',
  supplier_payment: 'Pago a proveedor',
};

export interface CashWithdrawalUpdate {
  amount?: number;
  category?: CashWithdrawalCategory;
  payment_method?: PaymentMethod;
  description?: string;
  recipient?: string;
}

export interface CashRegisterSummary {
  id: number;
  status: CashRegisterStatus;
  shift_number: number;
  opened_at: string;
  closed_at?: string;
  opening_cash: number;
  closing_cash?: number;
  total_sales: number;
  total_profit: number;
  cash_difference?: number;
  opened_by?: CashRegisterUser;
  close_type?: CloseType;
  auto_closed?: boolean;
}

export interface CurrentCashRegisterStatus {
  is_open: boolean;
  current_register?: CashRegister;
  last_closed_register?: CashRegisterSummary;
  // Info para turnos
  last_partial_close_today?: CashRegisterSummary;  // Último cierre parcial de hoy
  can_open_new_shift: boolean;  // True si se puede abrir nuevo turno
  next_shift_number: number;  // Número del próximo turno
}

export interface CashRegisterCreate {
  opening_cash: number;
  opening_notes?: string;
}

export interface CashRegisterClose {
  closing_cash: number;
  closing_notes?: string;
  close_type?: CloseType;  // "partial" para cierre de turno, "final" para cierre del día
}

export interface DailyProfit {
  date: string;
  total_sales: number;
  total_cost: number;
  total_expenses: number;
  total_profit: number;
  cash_difference: number;
}

export interface CashRegisterFilters {
  status?: CashRegisterStatus;
  date_from?: string;
  date_to?: string;
  limit?: number;
}

export const cashRegisterService = {
  getStatus: async (): Promise<CurrentCashRegisterStatus> => {
    const response = await api.get('/cash-register/status');
    return response.data;
  },

  getRegisters: async (filters?: CashRegisterFilters): Promise<CashRegisterSummary[]> => {
    const response = await api.get('/cash-register/', { params: filters });
    return response.data;
  },

  getRegister: async (id: number): Promise<CashRegister> => {
    const response = await api.get(`/cash-register/${id}`);
    return response.data;
  },

  openRegister: async (data: CashRegisterCreate): Promise<CashRegister> => {
    const response = await api.post('/cash-register/open', data);
    return response.data;
  },

  closeRegister: async (id: number, data: CashRegisterClose): Promise<CashRegister> => {
    const response = await api.post(`/cash-register/${id}/close`, data);
    return response.data;
  },

  reopenRegister: async (id: number): Promise<CashRegister> => {
    const response = await api.post(`/cash-register/${id}/reopen`);
    return response.data;
  },

  getProfitHistory: async (days: number = 30): Promise<DailyProfit[]> => {
    const response = await api.get('/cash-register/history/profit', { params: { days } });
    return response.data;
  },

  // === Salidas de Caja ===

  getWithdrawals: async (cashRegisterId?: number): Promise<CashWithdrawal[]> => {
    const response = await api.get('/cash-register/withdrawals/', {
      params: cashRegisterId ? { cash_register_id: cashRegisterId } : undefined
    });
    return response.data;
  },

  createWithdrawal: async (data: CashWithdrawalCreate): Promise<CashWithdrawal> => {
    const response = await api.post('/cash-register/withdrawals/', data);
    return response.data;
  },

  updateWithdrawal: async (id: number, data: CashWithdrawalUpdate): Promise<CashWithdrawal> => {
    const response = await api.put(`/cash-register/withdrawals/${id}`, data);
    return response.data;
  },

  deleteWithdrawal: async (id: number): Promise<void> => {
    await api.delete(`/cash-register/withdrawals/${id}`);
  },

  // === Categorías de salidas (por veterinaria) ===

  getCategories: async (includeInactive = false): Promise<WithdrawalCategory[]> => {
    const response = await api.get('/cash-register/categories', {
      params: includeInactive ? { include_inactive: true } : undefined,
    });
    return response.data;
  },

  createCategory: async (data: WithdrawalCategoryCreate): Promise<WithdrawalCategory> => {
    const response = await api.post('/cash-register/categories', data);
    return response.data;
  },

  updateCategory: async (id: number, data: WithdrawalCategoryUpdate): Promise<WithdrawalCategory> => {
    const response = await api.put(`/cash-register/categories/${id}`, data);
    return response.data;
  },

  deleteCategory: async (id: number): Promise<{ message: string }> => {
    const response = await api.delete(`/cash-register/categories/${id}`);
    return response.data;
  },
};

// Labels para categorías de salidas
// Nombres de las categorías que trae el sistema por defecto. Sirven de respaldo mientras
// no cargó la lista real de la veterinaria (que puede tenerlas renombradas).
export const WITHDRAWAL_CATEGORY_LABELS: Record<string, string> = {
  delivery: 'Cadete/Envío',
  transport: 'Transporte',
  supplies: 'Compras/Insumos',
  services: 'Servicios',
  withdrawal: 'Retiro de Dinero',
  supplier_payment: 'Pago a Proveedor',
  salary: 'Sueldo',
  other: 'Otro',
};

// Labels para métodos de pago
export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  cash: 'Efectivo',
  card: 'Tarjeta',
  transfer: 'Transferencia',
};

/**
 * Como nombrar el medio de pago de una venta en un listado.
 *
 * Ademas de los tres medios, el backend puede devolver 'split' cuando la venta se
 * cobro con mas de uno (pago dividido). Sin este caso el listado mostraba "split".
 */
export const paymentMethodLabel = (metodo?: string | null): string => {
  if (!metodo) return '-';
  if (metodo === 'split') return 'Dividido';
  return PAYMENT_METHOD_LABELS[metodo as PaymentMethod] || metodo;
};
